import React, { FC, RefObject, useEffect, useRef, useState, useCallback, useMemo } from 'react';
import clsx from 'clsx';
import { Box, Button, Grid, Tab, Tabs, Select, MenuItem, Typography, Dialog, Paper, DialogContent, Divider, Checkbox, DialogActions, IconButton,Alert, AlertTitle } from '@mui/material';
import { Refresh } from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { Header } from '../../components/header';
import { Help, Time, User } from '../../components/header/components';
import useStyles from './styles';
import { useMountEffect } from '../../globals/hooks/use-mount-effect';
import ConfigurationFilesLibrary from '../../services/config-files-library';
import BulkDeviceManagementTable from '../../components/bulk-device-management/table';
import {
  preTableHeadCells,
  postTableHeadCells,
  postTableSortableAccessors,
  preTableSortableAccessors,
  simCredentialingHeadCells,
  engineeringTemplateHeadCells,
  configAdminHeadCells,
  commandsTableSortableAccessors,
  modemOnlyFlashHeadCells,
  flashModemOnlySortableAccessors,
  scheduleCommandHeadCells,
} from '../../components/bulk-device-management/table/head-cells';
import FileUploader from '../../components/file-loader';
import {
  preFlashLogs,
  postFlashLogs,
  setLogTab,
  uploadBlendedFromCsv,
  uploadBlendedFromSearch,
  exportPostFlash,
  uploadSIMCSV,
  refreshSimRecredentialStatus,
  uploadFileToGetStatus,
  uploadModemFlash,
  refreshModemFlash,
  modemFlashStatus,
} from '../../store/bulk-device-flash/actions';
import { AppState } from '../../store';
import {
  downloadModemOnlyFlashTemplate,
  downloadSIMTemplate,
  downloadTemplate,
} from '../../components/bulk-device-management/csv-template';
import { FlashTabStrings, SelectedDevice, BulkFlashLogActionNames } from '../../store/bulk-device-flash/types';
import SearchBar from '../../components/search-bar';
import { TabContent, ActionTypes, SIMCredentialing, BootstrapType, } from './types';
import BulkSelectActions from '../../components/bulk-device-management/bulk-select-actions';
import LoadedContentButtons from '../../components/bulk-device-management/loaded-content-buttons.tsx';
import DownloadIcon from '../../assets/icons/download-icon';
import CampaignUploader from '../../components/bulk-device-management/campaign-uploader';
import ConfigAdminAccordions from '../../components/bulk-device-management/config-admin/config-admin-accordions';
import VirtualizedTable from '../../components/virtualized-table';
import { softwareHeadCells, softwareHeadCellsAccessors } from '../../components/bulk-device-management/queue-file/head-cells';
import CancelIcon from '../../assets/icons/cancel-icon';

const BulkDeviceManagement: FC = () => {
  const { client, envVariables } = useSelector((state: AppState) => state.login)
  const configFilesLibrary = new ConfigurationFilesLibrary(
    client,
    envVariables.configurationFilesLibraryV1BaseUrl,
  );
  const [actionType, setActionType] = useState<string>(localStorage.getItem('bulkDeviceManagementActionType') || 'Action Type');
  const [bootstrapActionType, setBootstrapActionType] = useState<string>('Bootstrap');
  const [selectedConfigAdminAction, setSelectedConfigAdminAction] = useState<string>('Action Type');
  const [columnVisibilityPre, setColumnVisibilityPre] = useState([]);
  const [columnOrderPre, setColumnOrderPre] = useState([]);
  const [columnVisibilityPost, setColumnVisibilityPost] = useState([]);
  const [columnOrderPost, setColumnOrderPost] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [columnVisibilityCampaign, setColumnVisibilityCampaign] = useState([]);
  const [columnOrderCampaign, setColumnOrderCampaign] = useState([]);
  const [columnVisibilitySIM, setColumnVisibilitySIM] = useState([]);
  const [columnOrderSIM, setColumnOrderSIM] = useState([]);
  const [columnVisibilityConfigAdmin, setColumnVisibilityConfigAdmin] = useState([]);
  const [columnOrderConfigAdmin, setColumnOrderConfigAdmin] = useState([]);
  const [columnVisibilityModemOnlyFlash, setColumnVisibilityModemOnlyFlash] = useState([]);
  const [columnOrderModemOnlyFlash, setColumnOrderModemOnlyFlash] = useState([]);
  const [popoverOpen, setPopoverOpen] = useState<boolean>(false);
  const [softwareTableData, setSoftwareTableData] = useState<any[] | [] | null>(null);
  const [selectedItem, setSelectedItem] = useState<number | null>();
  const [isLoading, setIsLoading] = useState<boolean>();
  const [softwareToFlash, setSoftwareToFlash] = useState<{
    id: string;
  }>();
  const [fileTypeId, setFileTypeId] = useState<number>(10348);
  const [fileToPush, setFileToPush] = useState<any>();
  const [scheduleCommandResponse, setScheduleCommandResponse] = useState<any[]>([]);
  const modemTableContainer = useRef<any>();
  const [showMessage, setShowMessage] = useState(false);
  const classes = useStyles();
  const tableContainer: RefObject<HTMLDivElement> = useRef(null);
  const dispatch = useDispatch();
  const preTableData = useSelector((state: AppState) => state.bulkFlash.preFlashLogs);
  const postTableData = useSelector((state: AppState) => state.bulkFlash.postFlashLogs);
  const simTableData = useSelector((state: AppState) => state.bulkFlash.simCredentialing);
  const configAdminTableData = useSelector((state: AppState) => state.commands.commands);
  const logTab = useSelector((state: AppState) => state.bulkFlash.logTab);
  const simCredentialingFile = useSelector(
    (state: AppState) => state.bulkFlash.simCredentialingFile,
  );
  const totalAcceptedDevices = useSelector(
    (state: AppState) => state.bulkFlash.totalAcceptedDevices,
  );
  const modemFlashFile = useSelector((state: AppState) => state.bulkFlash.modemFlashFile);
  const resetSelectedRows = useCallback(() => {
    if (totalAcceptedDevices === null) setSelectedRows([]);
  }, [totalAcceptedDevices]);
  const appPermissions = useSelector(
    (state: AppState) => state.entitlements?.authorizations?.appPermissions,
  );
  const modemFlashTableData = useSelector((state: AppState) => state.bulkFlash.modemFlashRecord);
  const message = "Flash file can't be queued during Caterpillar No FOTA window.";
  const selectedDate = new Set([13, 14, 15, 16, 17, 18, 26, 27, 28, 29, 30, 31]);

  const checkCampaignPermission = () =>
    appPermissions &&
    appPermissions.iot &&
    appPermissions.iot.fileTypeConfiguration &&
    appPermissions.iot.fileTypeConfiguration.includes('*');

  const handleConfigAdminAccordionClose = () => {
    setScheduleCommandResponse([]); // <-- always reset to empty array
    setSelectedConfigAdminAction('Action Type'); // Optionally reset action type
  };
  useEffect(() => resetSelectedRows(), [totalAcceptedDevices, resetSelectedRows]);
  const tabContent: TabContent[] = [
    {
      title: FlashTabStrings.preFlash,
      storageString: 'bulk-device-pre',
      headerColumns: preTableHeadCells,
      sortableAccessors: preTableSortableAccessors,
      setColumnVisibility: setColumnVisibilityPre,
      columnVisibility: columnVisibilityPre,
      setColumnOrder: setColumnOrderPre,
      columnOrder: columnOrderPre,
      tableContainer,
      onRowSelection: setSelectedRows,
      data: preTableData,
    },
    {
      title: FlashTabStrings.postFlash,
      storageString: 'bulk-device-post',
      headerColumns: postTableHeadCells,
      sortableAccessors: postTableSortableAccessors,
      setColumnVisibility: setColumnVisibilityPost,
      columnVisibility: columnVisibilityPost,
      setColumnOrder: setColumnOrderPost,
      columnOrder: columnOrderPost,
      tableContainer,
      data: postTableData,
    },
    {
      title: FlashTabStrings.simCredentialing,
      storageString: 'simCredentialing',
      headerColumns: localStorage.getItem('bulkDeviceManagementActionType') === 'Engineering Template' ? engineeringTemplateHeadCells : simCredentialingHeadCells,
      sortableAccessors: postTableSortableAccessors,
      setColumnVisibility: setColumnVisibilitySIM,
      columnVisibility: columnVisibilitySIM,
      setColumnOrder: setColumnOrderSIM,
      columnOrder: columnOrderSIM,
      tableContainer,
      data: simTableData,
    },
    {
      title: FlashTabStrings.configAdmin,
      storageString: 'configAdmin',
      headerColumns: selectedConfigAdminAction === 'schedule command'
        ? scheduleCommandHeadCells
        : configAdminHeadCells,
      sortableAccessors: commandsTableSortableAccessors,
      setColumnVisibility: setColumnVisibilityConfigAdmin,
      columnVisibility: columnVisibilityConfigAdmin,
      setColumnOrder: setColumnOrderConfigAdmin,
      columnOrder: columnOrderConfigAdmin,
      tableContainer,
      data: selectedConfigAdminAction === 'schedule command'
        ? Array.isArray(scheduleCommandResponse) ? scheduleCommandResponse : []
        : configAdminTableData,
    },
    {
      title: FlashTabStrings.modemFlash,
      storageString: 'modemFlash',
      headerColumns: modemOnlyFlashHeadCells,
      sortableAccessors: flashModemOnlySortableAccessors,
      setColumnVisibility: setColumnVisibilityModemOnlyFlash,
      columnVisibility: columnVisibilityModemOnlyFlash,
      setColumnOrder: setColumnOrderModemOnlyFlash,
      columnOrder: columnOrderModemOnlyFlash,
      tableContainer,
      data: modemFlashTableData,
    },
  ];
  if (checkCampaignPermission()) {
    tabContent.splice(2, 0, {
      title: FlashTabStrings.campaign,
      storageString: 'campaign',
      headerColumns: [],
      sortableAccessors: postTableSortableAccessors,
      setColumnVisibility: setColumnVisibilityCampaign,
      columnVisibility: columnVisibilityCampaign,
      setColumnOrder: setColumnOrderCampaign,
      columnOrder: columnOrderCampaign,
      tableContainer,
      data: [],
    });
  }

  const handleSwitchTab = (newValue: FlashTabStrings.preFlash | FlashTabStrings.postFlash) => {
    dispatch(setLogTab(newValue));
    if (newValue === FlashTabStrings.postFlash) dispatch(postFlashLogs({}));
    if (newValue === FlashTabStrings.preFlash) dispatch(preFlashLogs({}));
  };

  const handleSIMUpload = async (file: File) => {
    await dispatch(uploadSIMCSV(file, actionType, bootstrapActionType));
  };

  const handleStatus = async (file: File) => {
    await dispatch(uploadFileToGetStatus(file, actionType, bootstrapActionType));
  };

  const handleSIMRefresh = async () => {
    await dispatch(refreshSimRecredentialStatus(actionType));
  };

  const getSoftwareFiles = async () => {
    if (!softwareTableData && fileTypeId > 0) setSoftwareTableData([]);
    try {
      setIsLoading(true);
      const softwareResponse = await configFilesLibrary.getConfigFiles({
        fileTypeId: `${`10348`}`,
        isRetrievable: true,
      });
      if (softwareResponse?.data?.configs) {
        setSoftwareTableData(softwareResponse.data.configs);
      } else {
        setSoftwareTableData(null);
      }
      setIsLoading(false);
    } catch (error: any) {
      if (error?.response?.status === 404) {
        setSoftwareTableData(null);
      }
    }
  };
  useEffect(() => { if(logTab.includes('Modem Flash')&& fileToPush) getSoftwareFiles(); }, [(logTab.includes('Modem Flash')&& fileToPush)]);
  const firmwareData = useMemo(() => softwareTableData, [softwareTableData]);
  const data = firmwareData;
  const resetSelection = () => {
    setSoftwareToFlash(undefined);
    setSelectedItem(null);
    setFileToPush(null);
  };

  const Checkbox = ({ row }: any) => (
    <>
      <input
        type="radio"
        checked={row.id === selectedItem}
        onChange={() => {
          setSelectedItem(row.id);
          setSoftwareToFlash({
            id: row.original.id,
          });
        }}
      />
    </>
  );
  const checkColumn = {
    accessor: 'radio',
    width: 30,
    Header: '',
    component: Checkbox,
  };
  const columns = useMemo(() => [checkColumn, ...softwareHeadCells], [checkColumn]);
  const closePopover = () => {
    setPopoverOpen(false);
  }

  const showFotaMessage = () => {
    const currentDate = new Date();
    const currentDay = currentDate.getDate();
    if (selectedDate.has(currentDay)) {
      setShowMessage(true);
    } else { setShowMessage(false); }
  }

  const handelModemOnlyFlashUpload = async (file: File) => {
    setFileToPush(file);
    if (file) await setPopoverOpen(true);
  };

  const modemFlashDevices = async () => {
    closePopover();
     await dispatch(uploadModemFlash(fileToPush, softwareToFlash));
    resetSelection();
  }

  const handleModemFlashRefresh = async () => {
    await dispatch(refreshModemFlash());
  }
  
  const getModemFlashStatus = async (file: File) => {
    await dispatch(modemFlashStatus(file)); 
  }

  const actionTypeOptions: ActionTypes[] = [
    { title: 'SIM Credentialing', value: SIMCredentialing.SimCredential, disabled: false },
    { title: 'Engineering Template', value: SIMCredentialing.EngineeringTemplate, disabled: false },
  ];
  const bootstrapActionTypeOptions: ActionTypes[] = [
    { title: 'AT&T', value: BootstrapType.ATT, disabled: false },
    { title: 'Vodafone', value: BootstrapType.Vodafone, disabled: false },
  ];

  useEffect(() => showFotaMessage(), [logTab]);

  return (
    <>
      <Header
        title="Bulk Device Management"
        headerComponents={[<Help userLink='/SitePages/Bulk.aspx?promotedState=0' />, <Time suppressPopover />, <User />]}
      />
      <div className={classes.scrollBox}>
        <Box px={3} pt={1} pb={0} display="flex" justifyContent="space-between">
          <Tabs
            className={classes.tabs}
            value={logTab}
            onChange={(event, newValue) => handleSwitchTab(newValue)}
            indicatorColor="secondary"
            textColor="primary"
          >
            {tabContent.map((tab: TabContent) => (
              <Tab value={tab.title} label={tab.title} />
            ))}
          </Tabs>
        </Box>
        {tabContent.map((tabInstance: TabContent) => (
          <>
            {logTab === tabInstance.title && (
              <>
                {tabInstance.title === FlashTabStrings.preFlash && (
                  <Box p={4} pb={1} pt={2}>
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={6} lg={8}>
                        <Box display="flex">
                          <Box flexShrink={0}>
                            <FileUploader
                              text="Upload CSV"
                              handleFile={(file: any) => dispatch(uploadBlendedFromCsv(file))}
                            />
                            <Button
                              className={clsx(classes.button, classes.buttonMargin)}
                              variant="outlined"
                              size="small"
                              onClick={() => downloadTemplate()}
                            >
                              Download Template
                            </Button>
                          </Box>
                          <Box maxWidth="200px" width="100%" px={1} flexShrink={1}>
                            <SearchBar
                              selectDeviceCallback={(selectedDevice: SelectedDevice) =>
                                dispatch(uploadBlendedFromSearch(selectedDevice))
                              }
                            />
                          </Box>
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={6} lg={4}>
                        <Box
                          display="flex"
                          className={classes.directionalBreakpointSwitch}
                          alignItems="center"
                        >
                          {!!totalAcceptedDevices && totalAcceptedDevices > 0 && (
                            <Typography className={classes.count}>
                              {selectedRows.length > 0 && `${selectedRows.length} of `}
                              {`${totalAcceptedDevices} Record${totalAcceptedDevices > 1 ? 's' : ''
                                }`}
                            </Typography>
                          )}
                          {selectedRows.length > 0 && (
                            <BulkSelectActions selectedRows={selectedRows} />
                          )}
                          <LoadedContentButtons tabInstance={tabInstance} />
                        </Box>
                      </Grid>
                    </Grid>
                    {
                      showMessage &&
                      <Alert severity="warning" style={{ backgroundColor: '#ffd335', height: '38px' }} >
                        <AlertTitle> {message}</AlertTitle>
                      </Alert>
                    }
                  </Box>
                )}
                {tabInstance.title === FlashTabStrings.configAdmin && (
                  <ConfigAdminAccordions
                  onActionTypeChange={setSelectedConfigAdminAction}
                  setScheduleCommandResponse={setScheduleCommandResponse}
                  scheduleCommandResponse={Array.isArray(scheduleCommandResponse) ? scheduleCommandResponse : []}
                  onAccordionClose={handleConfigAdminAccordionClose} />
                )}

                {tabInstance.title === FlashTabStrings.postFlash && (
                  <Box p={4} pb={1} pt={2} display="flex" justifyContent="space-between">
                    <Box>
                      <Button
                        disabled={!(postTableData && postTableData.length > 0)}
                        className={classes.button}
                        onClick={() => dispatch(exportPostFlash())}
                        variant="outlined"
                        size="small"
                      >
                        Export <DownloadIcon className={classes.inButtonIcon} />
                      </Button>
                    </Box>
                    <LoadedContentButtons tabInstance={tabInstance} />
                  </Box>
                )}
                {tabInstance.title === 'Campaign' && checkCampaignPermission() && (
                  <>
                    {showMessage &&
                      <Alert severity="warning" style={{ backgroundColor: '#ffd335', height: '38px', margin: '10px' }} >
                        <AlertTitle> {message}</AlertTitle>
                      </Alert>
                    }
                    <CampaignUploader />
                  </>
                )}
                {tabInstance.title === FlashTabStrings.simCredentialing && (
                  <Box p={4} pb={1} pt={2} display="flex">

                    <Grid item xs={4} md={4} lg={2} className={classes.buttonMarginRight}>
                      <Select
                        variant="standard"
                        id="actionType"
                        label="Action Type"
                        value={actionType}
                        onChange={(event) => {
                          localStorage.setItem('bulkDeviceManagementActionType', event.target.value);
                          if (actionType !== event.target.value) {
                            dispatch({
                              type: BulkFlashLogActionNames.SET_SIM_CREDENTIALING,
                              payload: [],
                            });
                            setActionType(event.target.value as SIMCredentialing);
                            setBootstrapActionType('Bootstrap');
                          }
                        }
                        }
                        className={classes.select}
                      >
                        <MenuItem value="Action Type">
                          <em className={classes.unSelected}>Action Type</em>
                        </MenuItem>
                        {(actionTypeOptions as Array<ActionTypes>).map((o: ActionTypes) => (
                          <MenuItem value={o.value} disabled={o.disabled}>{o.title}</MenuItem>
                        ))}
                      </Select>
                    </Grid>

                    <Button
                      className={clsx(classes.button, classes.buttonMarginRight)}
                      disabled={(actionType === 'Action Type')}
                      onClick={() => downloadSIMTemplate(actionType)}
                      variant="outlined"
                      size="small"
                    >
                      Download Template
                    </Button>

                    {actionType === SIMCredentialing.EngineeringTemplate &&
                      <Grid item xs={4} md={4} lg={2} className={classes.buttonMarginRight}>
                        <Select
                          variant="standard"
                          id="bootstarpactionType"
                          label="Bootstrap Action Type"
                          value={bootstrapActionType}
                          onChange={(event) => {
                            setBootstrapActionType(event.target.value as BootstrapType);
                          }
                          }
                          className={classes.select}
                        >
                          <MenuItem value="Bootstrap">
                            <em className={classes.unSelected}>Bootstrap</em>
                          </MenuItem>
                          {(bootstrapActionTypeOptions as Array<ActionTypes>).map((o: ActionTypes) => (
                            <MenuItem value={o.value} disabled={o.disabled}>{o.title}</MenuItem>
                          ))}
                        </Select>
                      </Grid>
                    }

                    <Box className={classes.buttonMarginRight}>
                      <FileUploader
                        text="Upload CSV"
                        handleFile={handleSIMUpload}
                        // enabled={(actionType !== 'Action Type')}
                        enabled={(actionType === SIMCredentialing.SimCredential) ||
                          (actionType === SIMCredentialing.EngineeringTemplate
                            && bootstrapActionType !== 'Bootstrap')
                        }
                      />
                    </Box>

                    <Box className={classes.buttonMarginRight}>
                      <FileUploader
                        text="Get Status"
                        handleFile={handleStatus}
                        // enabled={(actionType !== 'Action Type')}
                        enabled={(actionType === SIMCredentialing.SimCredential) ||
                          (actionType === SIMCredentialing.EngineeringTemplate
                            && bootstrapActionType !== 'Bootstrap')
                        }
                      />
                    </Box>

                    {simCredentialingFile && (
                      <Button
                        className={clsx(classes.button, classes.marginLeftAuto)}
                        onClick={() => handleSIMRefresh()}
                        size="small"
                      >
                        <Refresh />
                      </Button>
                    )}
                  </Box>
                )}

                {tabInstance.title === FlashTabStrings.modemFlash && (
                  <Box p={4} pb={1} pt={2} display="flex">
                    <Button
                      className={clsx(classes.button, classes.buttonMarginRight)}
                      onClick={() => downloadModemOnlyFlashTemplate()}
                      variant="outlined"
                      size="small"
                    >
                      Download Template
                    </Button>
                    <Box className={classes.buttonMarginRight}>
                      <FileUploader
                        text="Upload CSV"
                        handleFile={handelModemOnlyFlashUpload}
                      />
                    </Box>
                    <Box className={classes.buttonMarginRight}>
                      <FileUploader
                        text="Get Status"
                        handleFile={getModemFlashStatus}
                      />
                    </Box>
                    {modemFlashFile &&  
                    <Button
                      className={clsx(classes.button, classes.marginLeftAuto)}
                      onClick={() => handleModemFlashRefresh()}
                      size="small"
                    >
                      <Refresh />
                    </Button>
                    }  

                  </Box>
                )}

                <div
                  ref={tableContainer}
                  className={classes.tableContainer}
                  hidden={tabInstance.title === 'Campaign'}
                >
                  <BulkDeviceManagementTable {...tabInstance} />
                </div>
              </>
            )}
          </>
        ))}
        <Dialog open={popoverOpen} PaperComponent={Paper} className={classes.dialog} maxWidth="lg">
          <Paper elevation={24}>
            <DialogContent>
              <Box p={2} >
                <Box pb={2}>
                  <Typography variant="h5" mb={1}>Queue Files</Typography>
                  <Divider />
                  {softwareTableData && (
                    <div ref={modemTableContainer} className={classes.modemTableContainer}>
                      <VirtualizedTable
                        sortableAccessors={softwareHeadCellsAccessors}
                        columns={columns}
                        data={data || []}
                        itemCount={data?.length || 0}
                        tableContainer={modemTableContainer}
                        loading={isLoading}
                      />
                    </div>
                  )}
                </Box>
                <Box
                  display="flex"
                  flexGrow={1}
                  alignItems="center"
                  justifyContent="flex-end"
                  pr={2}
                >
                  {selectedItem && <>
                    <Box px={1}>
                      <Typography variant="h6">
                        Selected Software:
                      </Typography>
                    </Box>
                    <Box px={1} flexGrow={0} overflow="hidden">
                      <Typography className={classes.overFlowEllipsis}>
                        {selectedItem && data && data[selectedItem].fileName}
                      </Typography>
                    </Box>
                    <Box px={1} pr={2}>
                      <IconButton
                        className={classes.iconButton}
                        id="queue-files-delete-selection"
                        onClick={() => resetSelection()}
                        aria-label="edit"
                        size="large"
                      >
                        <CancelIcon />
                      </IconButton>
                    </Box>
                  </>
                  }
                  <DialogActions>
                    <Button
                      onClick={() => closePopover()}
                      variant="outlined"
                      size="small"
                      color="primary"
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={modemFlashDevices}
                      variant="outlined"
                      size="small"
                      color="primary"
                      endIcon
                      disabled={!selectedItem}
                    >
                      Queue and Flash
                    </Button>
                  </DialogActions>
                </Box>
              </Box>
            </DialogContent>
          </Paper>
        </Dialog>
      </div>
    </>
  );
};

export default BulkDeviceManagement;